import type { SponsorState } from './types';
import { PERIODS, TIERS } from './catalog';

export const SPONSOR_STORAGE_KEY = 'lb-sponsor-v1';

export interface SponsorStorage {
  read(): SponsorState | null;
  write(state: SponsorState): boolean;
  subscribe(listener: () => void): () => void;
}

type StorageAccess = Pick<Storage, 'getItem' | 'setItem'>;
interface StorageEvents {
  addEventListener(type: 'storage', listener: (event: StorageEvent) => void): void;
  removeEventListener(type: 'storage', listener: (event: StorageEvent) => void): void;
}
type Row = Record<string, unknown>;

const isRow = (value: unknown): value is Row =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const strings = (row: Row, fields: string[]) => fields.every((field) => typeof row[field] === 'string');
const nonNegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;
const whole = (value: unknown): value is number => nonNegative(value) && Number.isSafeInteger(value);
const oneOf = (value: unknown, options: readonly string[]) => typeof value === 'string' && options.includes(value);
const date = (value: unknown) => typeof value === 'string' && Number.isFinite(Date.parse(value));

function isAccount(value: unknown): boolean {
  return isRow(value)
    && strings(value, ['id', 'company', 'email', 'phone', 'businessType', 'licence', 'licencePhoto'])
    && date(value.createdAt)
    && oneOf(value.status, ['registered', 'verified', 'rejected']);
}

function isAd(value: unknown): boolean {
  return isRow(value)
    && strings(value, ['id', 'title', 'type', 'showsIn'])
    && oneOf(value.status, ['checking', 'needs_changes', 'live', 'scheduled', 'ended'])
    && nonNegative(value.share) && value.share <= 1;
}

function isOrder(value: unknown): boolean {
  if (!isRow(value)) return false;
  const tier = TIERS.find((tier) => tier.id === value.tier);
  return strings(value, ['id', 'no', 'accountId', 'company', 'contact', 'phone', 'link'])
    && tier !== undefined
    && oneOf(value.segment, ['biz', 'ngo'])
    && oneOf(value.method, ['khqr', 'bank', 'card'])
    && oneOf(value.status, ['waiting_payment', 'in_review', 'needs_changes', 'live', 'ended'])
    && whole(value.stopIndex) && value.stopIndex < tier.prices.length
    && whole(value.periodMonths) && PERIODS.some((period) => period.months === value.periodMonths)
    && whole(value.months) && value.months > 0
    && ['list', 'paid', 'vat', 'total'].every((field) => nonNegative(value[field]))
    && ['createdAt', 'start', 'end'].every((field) => date(value[field]))
    && Array.isArray(value.provinces) && value.provinces.every((province) => typeof province === 'string')
    && Array.isArray(value.ads) && value.ads.every(isAd)
    && (value.changeNote === undefined || typeof value.changeNote === 'string');
}

function isPayment(value: unknown): boolean {
  return isRow(value)
    && strings(value, ['id', 'accountId', 'item', 'no'])
    && date(value.date) && nonNegative(value.amount)
    && oneOf(value.status, ['paid', 'waiting'])
    && (value.orderId === undefined || typeof value.orderId === 'string');
}

function isNotice(value: unknown): boolean {
  return isRow(value) && strings(value, ['id', 'accountId', 'text']) && date(value.at);
}

/** Persisted browser data is untrusted, even when its version number matches. */
export function isSponsorState(value: unknown): value is SponsorState {
  if (!isRow(value) || value.version !== 1) return false;
  if (!(value.currentAccountId === null || typeof value.currentAccountId === 'string')) return false;
  if (!Array.isArray(value.accounts) || !value.accounts.every(isAccount)) return false;
  if (!Array.isArray(value.orders) || !value.orders.every(isOrder)) return false;
  if (!Array.isArray(value.payments) || !value.payments.every(isPayment)) return false;
  if (!Array.isArray(value.notices) || !value.notices.every(isNotice)) return false;
  if (!isRow(value.extraPoints) || !Object.values(value.extraPoints).every(whole)) return false;
  return value.currentAccountId === null
    || value.accounts.some((account: Row) => account.id === value.currentAccountId);
}

export function createSponsorStorage(storage: StorageAccess | null, events?: StorageEvents): SponsorStorage {
  return {
    read() {
      try {
        const raw = storage?.getItem(SPONSOR_STORAGE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        return isSponsorState(parsed) ? parsed : null;
      } catch {
        return null;
      }
    },
    write(state) {
      if (!storage) return false;
      try {
        storage.setItem(SPONSOR_STORAGE_KEY, JSON.stringify(state));
        return true;
      } catch {
        // Quota/private-mode failures must not break the current demo session.
        return false;
      }
    },
    subscribe(listener) {
      const onStorage = (event: StorageEvent) => {
        if ((event.key === SPONSOR_STORAGE_KEY || event.key === null)
          && (!event.storageArea || event.storageArea === storage)) listener();
      };
      events?.addEventListener('storage', onStorage);
      return () => events?.removeEventListener('storage', onStorage);
    },
  };
}

export function createBrowserSponsorStorage(): SponsorStorage {
  if (typeof window === 'undefined') return createSponsorStorage(null);
  try {
    return createSponsorStorage(window.localStorage, window);
  } catch {
    return createSponsorStorage(null);
  }
}
